import type {Well} from '@/types/well';
import type {Well_AllQuery, Well_ByIdQuery, User_GetMyWellsQuery} from '@/graphql/generated/graphql.ts';

// Shared by WellsListPage (user_GetMyWells), DailyReportPage (well_ById),
// and BarrelTestsPage/RunTicketsPage/BarrelTestTrendPage (well_All) — all
// need to turn a real Well row into the Well shape the rest of the app
// renders.
//
// section/township/range come straight off the row (the backend added
// these fields to Well — see the regenerated generated-operations/*.graphql
// selection sets) instead of the placeholder '' this used to fall back to.
// leaseName is derived from the real lease relation (row.lease?.name) now
// that Well has a real leaseId/lease instead of a free-text leaseName
// field — see the comment on AdminWell.leaseId in adminTypes.ts for the
// fuller history.
//
// `downSince` and per-well `status` used to be stubbed here (Well had no
// downSince, and DailyWellReport had no wellId to correlate a report back
// to a particular well) — both gaps are now closed. downSince comes
// straight off the row below. `status` still can't be computed from a
// Well row alone though — it depends on report history, not just the well
// itself — so it's left as an optional param here (defaults to 'Pending')
// and the real value is computed by the caller via
// lib/wellStatus.ts's computeWellStatus and merged in. See WellsListPage
// for where that happens. `barrel` (added 2026-09-01, WellsListPage's
// "Barrel Total" column) is the same shape of gap — today's oil
// production depends on report history too — so it's the same kind of
// optional param, defaulting to null, merged in by WellsListPage from
// its already-loaded reportWindowByWellId.
export function toWell(
   row:
      | NonNullable<Well_AllQuery['well_All']>[number]
      | NonNullable<Well_ByIdQuery['well_ById']>
      | NonNullable<User_GetMyWellsQuery['user_GetMyWells']>[number],
   status: Well['status'] = 'Pending',
   barrel: Well['barrel'] = null,
   // Same kind of gap as `barrel` above — today's gas/water production and
   // the well's true last-reported date all depend on report history, not
   // the Well row itself. See WellsListPage.tsx's reportWindowByWellId
   // (gas/water) and lastReportedDateByWellId (lastReportedDate) for where
   // these are actually derived and merged in (added 2026-09-04 for the
   // Wells list "Gas Production"/"Water Production"/"Last Reported"
   // columns).
   gas: Well['gas'] = null,
   water: Well['water'] = null,
   lastReportedDate: Well['lastReportedDate'] = null,
   // Chunk 7 (2026-09-30) — same optional-param, caller-merges-it-in gap
   // as barrel/gas/water/lastReportedDate above; see the doc comments on
   // Well.tankDropBbls/tankDropFlagged/tankDropDate in types/well.ts.
   tankDropBbls: Well['tankDropBbls'] = null,
   tankDropFlagged: Well['tankDropFlagged'] = false,
   tankDropDate: Well['tankDropDate'] = null,
   pumperComments: Well['pumperComments'] = null,
): Well | null {
   if (!row) return null;
   return {
      id: String(row.id),
      wellName: row.name,
      leaseName: row.lease?.name ?? '',
      // Well_All.graphql and User_GetMyWells.graphql both select leaseId;
      // Well_ById.graphql still deliberately drops it (see that file's own
      // header comment) — the 'in' check keeps this one mapper safe across
      // all three row shapes instead of splitting into per-query mappers.
      // (User_GetMyWells added it back 2026-09-30 — see that file's header
      // comment; it was silently returning null here for every row before
      // that, an unnoticed bug from chunk 3's PulledBottomsPage work.)
      leaseId: 'leaseId' in row && row.leaseId != null ? String(row.leaseId) : null,
      section: row.section ?? '',
      township: row.township ?? '',
      range: row.range ?? '',
      locationCode: row.locationCode ?? '',
      status,
      lat: row.latitude ?? null,
      lng: row.longitude ?? null,
      active: !row.isDeleted,
      assignedUserIds: (row.userIdList ?? []).filter((id): id is NonNullable<typeof id> => id != null).map(String),
      downSince: row.downSince != null ? String(row.downSince) : null,
      // restartedOn added 2026-09-11 alongside downSince (see the doc
      // comment on Well.restartedOn in types/well.ts) — same mapping
      // convention.
      restartedOn: row.restartedOn != null ? String(row.restartedOn) : null,
      // Same null-to-'' convention as adminMappers.ts's toAdminWell —
      // identifier is now selected by all three of this function's row
      // shapes (Well_All/Well_ById/User_GetMyWells — see each .graphql
      // file), added to User_GetMyWells.graphql specifically so this
      // union access compiles; WellsListPage itself still has no reason
      // to display it.
      identifier: row.identifier != null ? String(row.identifier) : '',
      apiNumber: row.apiNumber ?? '',
      productionTypeNames: (row.productionTypeNameList ?? []).filter((n): n is string => typeof n === 'string'),
      productFieldNames: (row.productFieldNameList ?? []).filter((n): n is string => typeof n === 'string'),
      barrel,
      gas,
      water,
      lastReportedDate,
      tankDropBbls,
      tankDropFlagged,
      tankDropDate,
      pumperComments,
   };
}
